import { useState } from "react";
import { ReportButton } from "@/components/moderation/ReportButton";
import { RulingsPanel } from "@/components/rules/RulingsPanel";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FlipHorizontal, Pencil, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DFC_LAYOUTS, RARITY_CLS, layoutLabel } from "@/lib/mtg";
import { ManaCost } from "@/components/common/ManaCost";
import { ManaText } from "@/components/common/ManaSymbol";
import { SchemerPreview } from "@/components/cards/SchemeFields";
import { ImageReviewBanner, ReplaceFaceImage } from "@/components/cards/ReplaceFaceImage";
import { ColorPips } from "@/components/common/ColorPips";
import { RoleBadge, VisibilityBadge } from "@/components/common/Badges";
import { PageLoader } from "@/components/common/PageLoader";
import { EmptyState } from "@/components/common/PageParts";
import { frameLabel, shapeLabel } from "@/lib/planar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function CardImage({ card, flipped }) {
  const back = card.faces[1]?.image_url;
  return (
    <div className="flip-scene">
      <div className={`flip-inner relative ${flipped ? "flipped" : ""}`} style={{ aspectRatio: card.dimensions?.aspect_ratio || 63 / 88 }} data-testid="card-detail-image">
        <img src={assetUrl(card.faces[0].image_url)} alt={card.faces[0].name} className={`flip-face absolute inset-0 h-full w-full rounded-2xl border border-amber-900/50 ${card.design?.source === "external" || card.import_ref ? "object-contain" : "object-cover"} shadow-[0_0_40px_rgba(217,119,6,0.15)]`} />
        {back && <img src={assetUrl(back)} alt={card.faces[1].name} className={`flip-face flip-back absolute inset-0 h-full w-full rounded-2xl border border-amber-900/50 ${card.import_ref ? "object-contain" : "object-cover"}`} />}
      </div>
    </div>
  );
}

function FaceText({ face, card }) {
  const schemer = card.scheme_subtype === "legendary_schemer";
  return (
    <div className="panel p-6" data-testid="card-face-text">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-heading text-xl text-amber-100">{face.name}</h2>
        <ManaCost cost={face.mana_cost} size="lg" />
      </div>
      {face.type_line && <p className="mt-2 border-b border-[#2A3447] pb-3 text-sm font-semibold text-slate-300">{face.type_line}</p>}
      {face.oracle_text && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-200"><ManaText text={face.oracle_text} /></p>}
      {schemer && <div className="mt-4"><SchemerPreview loyalty={face.loyalty} abilities={card.custom_properties?.schemer_abilities || []} restrictions={card.custom_properties?.schemer_restrictions} testid="card-detail-schemer" /></div>}
      {face.flavor_text && <p className="mt-4 font-heading text-sm italic text-slate-400">{face.flavor_text}</p>}
      {!schemer && (face.power || face.toughness || face.loyalty || face.defense) && (
        <p className="mt-4 text-right font-mono text-lg font-semibold text-amber-200">{face.loyalty ? `Loyalty ${face.loyalty}` : face.defense ? `Defense ${face.defense}` : `${face.power ?? "–"}/${face.toughness ?? "–"}`}</p>
      )}
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[#2A3447] py-2.5 text-sm last:border-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="min-w-0 break-words text-right text-slate-200">{children}</dd>
    </div>
  );
}

function OwnerActions({ card }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const remove = async () => {
    try {
      await api.delete(`/cards/${card.card_id}`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Card removed");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="flex gap-3">
      <Link to={`/cards/${card.card_id}/${card.design?.source === "external" ? "edit-planar" : "edit"}`} className="btn-outline flex-1" data-testid="card-edit-button"><Pencil className="h-4 w-4" />Edit</Link>
      <AlertDialog>
        <AlertDialogTrigger asChild><button className="btn-danger" data-testid="card-delete-button"><Trash2 className="h-4 w-4" />Delete</button></AlertDialogTrigger>
        <AlertDialogContent className="border-[#2A3447] bg-[#121620] text-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-amber-100">Delete {card.name}?</AlertDialogTitle>
            <AlertDialogDescription>The card will be removed from the library. Its permanent ID will not be reused.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="card-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-600" data-testid="card-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function VersionHistory({ cardId }) {
  const { data } = useQuery({ queryKey: ["cards", "versions", cardId], queryFn: () => api.get(`/cards/${cardId}/versions`).then((r) => r.data) });
  if (!data) return null;
  return (
    <section className="panel p-6" data-testid="card-version-history">
      <p className="eyebrow mb-4">Version history</p>
      <ol className="relative space-y-4 border-l border-amber-700/40 pl-5">
        <li><span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-aether-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
          <p className="text-sm text-slate-100">v{data.current_version} · current</p>
          <p className="font-mono text-xs text-slate-500">{new Date(data.current_updated_at).toLocaleString()}</p></li>
        {data.items.map((v) => (
          <li key={v.version} data-testid={`card-version-${v.version}`}>
            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-amber-500 bg-[#101828]" />
            <p className="text-sm text-slate-300">v{v.version} · {v.snapshot.name} <span className="text-xs capitalize text-slate-500">({v.snapshot.rarity}, {v.snapshot.visibility})</span></p>
            <p className="font-mono text-xs text-slate-500">replaced {new Date(v.replaced_at).toLocaleString()}{v.edited_by_username ? ` by @${v.edited_by_username}` : ""}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function CardDetail() {
  const { cardId } = useParams();
  const { user, can } = useAuth();
  const [flipped, setFlipped] = useState(false);
  const { data: card, isLoading, isError } = useQuery({ queryKey: ["cards", "detail", cardId], queryFn: () => api.get(`/cards/${cardId}`).then((r) => r.data), retry: false });

  if (isLoading) return <PageLoader />;
  if (isError) return <div className="container-x py-24"><EmptyState title="Card not found" testid="card-not-found">It may be private or removed.</EmptyState></div>;

  const dfc = DFC_LAYOUTS.includes(card.layout);
  const mine = user && (user.user_id === card.owner_id || can("content.moderate"));
  const copyId = () => navigator.clipboard?.writeText(card.card_id).then(() => toast.success("Card ID copied"));

  return (
    <div className="container-x grid grid-cols-1 gap-12 py-12 lg:grid-cols-12" data-testid="card-detail-page">
      <div className="lg:col-span-5">
        <div className="mx-auto max-w-md lg:sticky lg:top-24">
          <CardImage card={card} flipped={flipped} />
          {dfc && (
            <button className="btn-outline mt-5 w-full" onClick={() => setFlipped((f) => !f)} aria-pressed={flipped} data-testid="card-flip-button">
              <FlipHorizontal className="h-4 w-4" />Show {flipped ? "front" : "back"} face
            </button>
          )}
        </div>
      </div>
      <div className="space-y-6 lg:col-span-7">
        <div>
          <p className="eyebrow mb-3">{layoutLabel(card.layout)} · Custom card</p>
          <h1 className="font-heading text-3xl font-bold text-amber-100 sm:text-4xl" data-testid="card-detail-name">{card.name}</h1>
          <ReportButton targetType="card" targetId={card.card_id} ownerId={card.owner_id} label="Report card or image" className="mt-1 px-0" />
          {card.owner && (
            <Link to={`/u/${card.owner.username}`} className="mt-3 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-200" data-testid="card-detail-creator-link">
              by <span className="font-semibold text-slate-200">{card.owner.display_name}</span> @{card.owner.username}
              <RoleBadge role={card.owner.role} />
            </Link>
          )}
        </div>
        {card.faces.map((f, i) => <FaceText key={i} face={f} card={card} />)}
        <dl className="panel px-6 py-3" data-testid="card-detail-meta">
          <Meta label="Rarity"><span className={`rounded border px-2 py-0.5 font-mono text-xs uppercase ${RARITY_CLS[card.rarity]}`}>{card.rarity}</span></Meta>
          <Meta label="Colors"><ColorPips colors={card.colors} /></Meta>
          <Meta label="Color identity"><ColorPips colors={card.color_identity} /></Meta>
          <Meta label="Mana value">{card.mana_value}</Meta>
          <Meta label="Set">{card.set ? <Link to={`/sets/${card.set.set_id}`} className="text-amber-300 hover:underline" data-testid="card-detail-set-link">{card.set.code} — {card.set.name}</Link> : "—"}</Meta>
          {card.collector_number && <Meta label="Collector #">{card.collector_number}</Meta>}
          <Meta label="Visibility"><VisibilityBadge visibility={card.visibility} /></Meta>
          {card.card_back && (
            <Meta label="Card back"><span className="inline-flex items-center gap-2"><img src={assetUrl(card.card_back.image_url)} alt="" className="h-10 w-7 rounded object-cover" />{card.card_back.name}</span></Meta>
          )}
          {card.faces[0].artist && <Meta label="Artist">{card.faces[0].artist}</Meta>}
          {card.design?.source === "external" && <>
            <Meta label="Design"><span data-testid="card-detail-external">Uploaded design · {frameLabel(card.design.visual_frame)} · plays as {card.specialty_type}</span></Meta>
            {card.design.credited_creator && <Meta label="Card creator"><span data-testid="card-detail-credited">{card.design.credited_creator}</span></Meta>}
            {card.design.chaos_ability && <Meta label="Chaos ability"><span className="whitespace-pre-line" data-testid="card-detail-chaos-ability">{card.design.chaos_ability}</span></Meta>}
            {card.design.image_width && <Meta label="Original image">{shapeLabel(card.design.image_width, card.design.image_height)}</Meta>}
          </>}
          {card.source_program && <Meta label="Created with">{card.source_program}</Meta>}
          {card.mechanics?.length > 0 && <Meta label="Mechanics">{card.mechanics.join(", ")}</Meta>}
          {card.supertypes?.length > 0 && <Meta label="Supertypes"><span data-testid="card-detail-supertypes">{card.supertypes.join(" ")}</span></Meta>}
          {card.types?.length > 0 && <Meta label="Card types"><span data-testid="card-detail-types">{card.types.join(" ")}</span></Meta>}
          {card.subtypes?.length > 0 && <Meta label="Subtypes">{card.subtypes.join(" ")}</Meta>}
          {card.category && <Meta label="Category"><span className="capitalize" data-testid="card-detail-category">{(card.custom_category || card.specialty_type || card.category).replace(/_/g, " ")}</span></Meta>}
          {card.dimensions && <Meta label="Physical size"><span data-testid="card-detail-dimensions">{card.dimensions.width_mm}×{card.dimensions.height_mm} mm · {card.dimensions.orientation} ({card.dimensions.preset})</span></Meta>}
          {card.publication_status === "draft" && <Meta label="Publication"><span className="font-mono text-xs uppercase text-copper-300" data-testid="card-detail-draft">Draft</span></Meta>}
          {Object.entries(card.custom_properties || {}).map(([k, v]) => <Meta key={k} label={k.replace(/_/g, " ")}><span data-testid={`card-detail-prop-${k}`}>{Array.isArray(v) ? v.map((r) => r.label || r.text).join(" · ") : String(v)}</span></Meta>)}
          {card.related_card_ids?.length > 0 && <Meta label="Related tokens / emblems">{card.related_card_ids.map((id) => <Link key={id} to={`/cards/${id}`} className="ml-2 font-mono text-xs text-amber-300 hover:underline" data-testid={`card-detail-related-${id}`}>{id}</Link>)}</Meta>}
          <Meta label="Version">v{card.version || 1}</Meta>
          {card.tags?.length > 0 && <Meta label="Tags">{card.tags.join(", ")}</Meta>}
          <Meta label="Permanent ID">
            <button onClick={copyId} className="inline-flex min-w-0 items-center gap-1.5 break-all text-left font-mono text-xs text-slate-400 hover:text-amber-200" data-testid="card-copy-id-button">
              {card.card_id}<Copy className="h-3 w-3" />
            </button>
          </Meta>
        </dl>
        {mine && card.dimension_warnings?.length > 0 && (
          <div className="rounded-lg border border-amber-600/50 bg-amber-950/30 p-4 text-sm text-amber-200" data-testid="card-dimension-warnings">{card.dimension_warnings.join(" ")}</div>
        )}
        {mine && <ImageReviewBanner card={card} />}
        {mine && <OwnerActions card={card} />}
        {mine && <ReplaceFaceImage card={card} />}
        <VersionHistory cardId={card.card_id} />
        <RulingsPanel targetType="custom_card" targetId={card.card_id} ownerId={card.owner_id} />
      </div>
    </div>
  );
}
